import { defineStore } from 'pinia';
import http from '../api/httpRequest.js'
import tenantEndpoint from '../api/tenantEndpoint.js';

const DEFAULT_CONFIG_CACHE_WINDOW_MS = 30 * 1000;

function createDefaultConnectionState() {
	return {
		phase: 'IDLE',
		hint: '',
		retryCount: 0
	};
}

function createDefaultGlobalConfig() {
	return {
		registration: {
			mode: ['username', 'phone'],
			ipLimit: 0,
			deviceLimit: 0
		},
		group: {
			maxMemberCount: 50000,
			messageTopLimit: 10,
			sendRateLimit: 0
		},
		session: {
			topLimit: 10
		},
		tenant: {
			available: true
		},
		friend: {
			defaultToDefaultAddable: false
		},
		upload: {
			imageMaxMb: 500,
			fileMaxMb: 500,
			videoMaxMb: 500,
			audioMaxMb: 500
		}
	};
}

function mergeGlobalConfig(config = {}) {
	const defaults = createDefaultGlobalConfig();
	return {
		registration: { ...defaults.registration, ...(config.registration || {}) },
		group: { ...defaults.group, ...(config.group || {}) },
		session: { ...defaults.session, ...(config.session || {}) },
		tenant: { ...defaults.tenant, ...(config.tenant || {}) },
		friend: { ...defaults.friend, ...(config.friend || {}) },
		upload: { ...defaults.upload, ...(config.upload || {}) }
	};
}

function createConfigSnapshot(store) {
	return {
		webrtc: store.webrtc,
		security: store.security,
		globalConfig: store.globalConfig
	};
}

function resolveCurrentTenantKey() {
	const session = tenantEndpoint.getCurrentSession ? tenantEndpoint.getCurrentSession() : null;
	return String((session && session.tenantKey) || '').trim().toLowerCase();
}

let configLoadPromise = null;
let configLoadPromiseTenantKey = '';
let configLoadPromiseAccessToken = '';
let configLoadedAccessToken = '';

export default defineStore('configStore', {
	state: () => {
		return {
			appInit: false,  // 实时链路是否已完成首次初始化
			workspaceReady: false, // 基础数据和缓存会话是否已可展示
			electronMode: false, // 当前是否以客户端模式运行
			theme: 'light', // 'light' | 'dark' | 'system'
			// 响应式状态
			isMobile: false,
			isDesktop: true,
			webrtc: {},
			security: {},
			connection: createDefaultConnectionState(),
			updater: {
				configured: false,
				tenantKey: '',
				feedUrl: '',
				phase: 'idle',
				trigger: '',
				currentVersion: '',
				latestVersion: '',
				releaseNotes: '',
				releaseDate: '',
				message: '',
				errorMessage: '',
				checking: false,
				available: false,
				downloading: false,
				downloaded: false,
				installing: false,
				progressPercent: 0
			},
			globalConfig: createDefaultGlobalConfig(),
			configLoadedTenantKey: '',
			configLoadedAt: 0
		}
	},
	actions: {
		resetLoadedConfigState() {
			this.webrtc = {};
			this.security = {};
			this.globalConfig = createDefaultGlobalConfig();
			this.configLoadedTenantKey = '';
			this.configLoadedAt = 0;
			configLoadedAccessToken = '';
		},
		setConfig(config) {
			const payload = config && typeof config === 'object' ? config : {};
			this.webrtc = payload.webrtc && typeof payload.webrtc === 'object' ? payload.webrtc : {};
			this.setSecurity(payload.security || {});
			this.setGlobalConfig(payload.globalConfig || {});
		},
		setSecurity(security) {
			this.security = security || {};
		},
		setGlobalConfig(config) {
			this.globalConfig = mergeGlobalConfig(config || {});
		},
		setElectronMode(mode) {
			this.electronMode = mode
		},
		setUpdaterState(patch = {}) {
			this.updater = {
				...this.updater,
				...patch
			};
		},
		resetUpdaterState(currentVersion = '') {
			this.updater = {
				configured: false,
				tenantKey: '',
				feedUrl: '',
				phase: 'idle',
				trigger: '',
				currentVersion: currentVersion || this.updater.currentVersion || '',
				latestVersion: '',
				releaseNotes: '',
				releaseDate: '',
				message: '',
				errorMessage: '',
				checking: false,
				available: false,
				downloading: false,
				downloaded: false,
				installing: false,
				progressPercent: 0
			};
		},
		setAppInit(appInit) {
			this.appInit = appInit;
		},
		setWorkspaceReady(ready) {
			this.workspaceReady = Boolean(ready);
		},
		setConnectionState(phase, hint = '') {
			this.connection = {
				...this.connection,
				phase: phase || 'IDLE',
				hint: hint || ''
			};
		},
		setConnectionRetryCount(retryCount) {
			const parsed = Number(retryCount);
			this.connection = {
				...this.connection,
				retryCount: Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0
			};
		},
		resetConnectionState() {
			this.connection = createDefaultConnectionState();
		},
		setTheme(theme) {
			this.theme = theme;
			localStorage.setItem('im-theme', theme);
			this.applyTheme();
		},
		applyTheme() {
			let resolvedTheme = this.theme;
			if (this.theme === 'system') {
				resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
			}
			document.documentElement.setAttribute('data-theme', resolvedTheme);
		},
		initTheme() {
			const saved = localStorage.getItem('im-theme');
			if (saved && ['light', 'dark', 'system'].includes(saved)) {
				this.theme = saved;
			}
			this.applyTheme();

			// 监听系统偏好变化
			const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
			mediaQuery.addEventListener('change', () => {
				if (this.theme === 'system') {
					this.applyTheme();
				}
			});
		},
		loadConfig(options = {}) {
			const tenantKey = resolveCurrentTenantKey();
			const accessToken = sessionStorage.getItem('accessToken') || '';
			const force = Boolean(options.force);
			const maxAgeMs = Number.isFinite(Number(options.maxAgeMs)) && Number(options.maxAgeMs) >= 0
				? Number(options.maxAgeMs)
				: DEFAULT_CONFIG_CACHE_WINDOW_MS;
			const cacheFresh = !force
				&& tenantKey
				&& tenantKey === this.configLoadedTenantKey
				&& accessToken === configLoadedAccessToken
				&& this.configLoadedAt > 0
				&& Date.now() - this.configLoadedAt <= maxAgeMs;
			if (cacheFresh) {
				return Promise.resolve(createConfigSnapshot(this));
			}
			if (configLoadPromise && configLoadPromiseTenantKey === tenantKey
				&& configLoadPromiseAccessToken === accessToken) {
				return configLoadPromise;
			}
			if (this.configLoadedTenantKey && tenantKey && this.configLoadedTenantKey !== tenantKey) {
				this.resetLoadedConfigState();
			}
			const requestPromise = http({
				url: '/system/config',
				method: 'GET'
			}).then((config) => {
				// 不让旧租户/旧登录会话的迟到配置覆盖当前会话。
				if (tenantKey !== resolveCurrentTenantKey()
					|| accessToken !== (sessionStorage.getItem('accessToken') || '')) {
					return this.loadConfig();
				}
				console.log('系统配置', config);
				this.setConfig(config);
				this.configLoadedTenantKey = tenantKey;
				this.configLoadedAt = Date.now();
				configLoadedAccessToken = accessToken;
				return config;
			}).finally(() => {
				if (configLoadPromise === requestPromise) {
					configLoadPromise = null;
					configLoadPromiseTenantKey = '';
					configLoadPromiseAccessToken = '';
				}
			});
			configLoadPromise = requestPromise;
			configLoadPromiseTenantKey = tenantKey;
			configLoadPromiseAccessToken = accessToken;
			return requestPromise;
		},
		// 初始化响应式检测
		initResponsive() {
			this._updateDeviceType();
			// 监听断点变化（仅 Mobile / Desktop 两档）
			const mqMobile = window.matchMedia('(max-width: 767px)');
			mqMobile.addEventListener('change', () => this._updateDeviceType());
		},
		// 根据窗口宽度更新设备类型
		_updateDeviceType() {
			const isMobile = window.innerWidth < 768;
			this.isMobile = isMobile;
			this.isDesktop = !isMobile;
		}
	}
});
